import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {geographyAssignmentValueValidator, UserAdditionalColumnType as UserColumnType} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplateAdditionalColumns";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

import type {GeographyCollections} from "~/contexts/geographyContext";

const coordinateAdditionalColumnTypes = new Set<string>([
    UdoColumnType.geographyLatitude,
    UdoColumnType.geographyLongitude,
]);

const referenceAdditionalColumnTypes = new Set<string>([
    UdoColumnType.geographyCountries,
    UdoColumnType.geographyIndiaZones,
    UdoColumnType.geographyIndiaStates,
    UdoColumnType.geographyIndiaCities,
    UdoColumnType.geographyIndiaPincodes,
]);

let referenceValuesByAdditionalColumnType: ReadonlyMap<string, ReadonlyMap<Uuid, string>> | null = null;

const userGeographyTypes = new Map<string, UdoColumnType>([
    [UserColumnType.geographyCountries, UdoColumnType.geographyCountries],
    [UserColumnType.geographyIndiaZones, UdoColumnType.geographyIndiaZones],
    [UserColumnType.geographyIndiaStates, UdoColumnType.geographyIndiaStates],
    [UserColumnType.geographyIndiaCities, UdoColumnType.geographyIndiaCities],
    [UserColumnType.geographyIndiaPincodes, UdoColumnType.geographyIndiaPincodes],
    [UserColumnType.geographyLatitude, UdoColumnType.geographyLatitude],
    [UserColumnType.geographyLongitude, UdoColumnType.geographyLongitude],
]);

/** User geography references are arrays; UDO geography references are scalar IDs. */
export function resolveUserGeographyAdditionalColumnValue(type: string, value: unknown): string | null {
    const udoType = userGeographyTypes.get(type);
    if (udoType === undefined) {
        return null;
    }
    if (coordinateAdditionalColumnTypes.has(udoType)) {
        return resolveGeographyAdditionalColumnValue(udoType, value) ?? "-";
    }
    if (value === null || value === undefined) {
        return "-";
    }
    const assignments = geographyAssignmentValueValidator.safeParse(value);
    if (!assignments.success) {
        return "Invalid geography assignment";
    }
    const labels = referenceValuesByAdditionalColumnType?.get(udoType);
    return assignments.data.map((id) => labels?.get(id) ?? id).join(", ") || "-";
}

export function initializeGeographyValueResolver(collections: GeographyCollections): void {
    const countriesById = new Map(collections.countries.map((country) => [country.id, country.name]));
    const indiaCitiesById = new Map(collections.indiaCities.map((city) => [city.id, city.name]));
    const indiaPincodesById = new Map(collections.indiaPincodes.map((pincode) => [pincode.id, pincode.pincode]));
    const indiaStatesById = new Map(collections.indiaStates.map((state) => [state.id, state.name]));
    const indiaZonesById = new Map(collections.indiaZones.map((zone) => [zone.id, zone.name]));

    referenceValuesByAdditionalColumnType = new Map<string, ReadonlyMap<Uuid, string>>([
        [UdoColumnType.geographyCountries, countriesById],
        [UdoColumnType.geographyIndiaZones, indiaZonesById],
        [UdoColumnType.geographyIndiaStates, indiaStatesById],
        [UdoColumnType.geographyIndiaCities, indiaCitiesById],
        [UdoColumnType.geographyIndiaPincodes, indiaPincodesById],
    ]);
}

export function isGeographyAdditionalColumnType(additionalColumnType: unknown): boolean {
    return typeof additionalColumnType === "string"
        && (coordinateAdditionalColumnTypes.has(additionalColumnType) || referenceAdditionalColumnTypes.has(additionalColumnType));
}

export function resolveGeographyAdditionalColumnValue(additionalColumnType: string, value: unknown): string | null {
    if (coordinateAdditionalColumnTypes.has(additionalColumnType)) {
        if (typeof value === "number") {
            return String(value);
        }
        return typeof value === "string" && value.trim() !== "" ? value : "-";
    }

    if (typeof value !== "string") {
        return "-";
    }
    if (referenceValuesByAdditionalColumnType === null) {
        return null;
    }

    const valuesById = referenceValuesByAdditionalColumnType.get(additionalColumnType);
    return valuesById?.get(value as Uuid) ?? (valuesById === undefined ? null : "-");
}
